import React, { FC } from "react";
import { MessageMiddleware, MessageMiddlewareContext } from "../../middlewares";
import { StandardMiddlewares } from "../../middlewares/standard-middleware";
import { ChatDetail, ChatMsgDetail } from "../../store/ChatAction";
import { getTimeAgo, isAgent } from "../../utils";
import { ChatServerMessage } from "../../xapp/ChatServerMessage";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import { ChatMarkdownMessage } from "../ChatMarkdownMessage/ChatMarkdownMessage";
import { ChatMessagePart } from "../ChatMessagePart";
import { ChatPermissionMessage } from "../ChatPermissionMessage";
import { ChatTextMessage } from "../ChatTextMessage/ChatTextMessage";
import { ChatScheduleWidget } from "../ChatScheduleWidget";
import "./ChatMessage.scss";

export interface ChatMessageProps {
    message: ChatMsgDetail;
    sibling?: boolean;
    agent: ChatUserInfo;
    readonly messageMiddleware?: MessageMiddleware;
    readonly middlewareContext?: MessageMiddlewareContext;
    minimizeOnClick?: () => void;
}

function getClassName(msg: ChatDetail) {
    return isAgent(msg.user.nick) ? "agent" : "visitor"
}

declare type AvaKey = keyof ChatServerMessage;

const avaKeys: readonly AvaKey[] = ["text", "html", "card", "list"]

/**
 * Chat Message Bubble with an Avatar
 * 
 * @param props 
 * @returns 
 */
const ChatMessage: FC<React.PropsWithChildren<ChatMessageProps>> = props => {

    const middleware = props.messageMiddleware || StandardMiddlewares;

    const ctx = props.middlewareContext;
    const user = props.agent;

    function renderByType() {

        const msg = props.message.msg;

        switch (props.message.type) {
            // TODO: props actually requires it to be "chat.msg". Fix prop typing?
            case "chat.msg":

                // Here is the deal. If we have text (output speech), then text - card - list - options
                // OR card OR list only. Avatar with text bubble.

                const avaKey = avaKeys.find(key => !!msg[key]);

                return (
                    <>
                        {msg.text &&
                            <ChatMessagePart showAvatar={avaKey === "text"} user={user}>
                                <ChatTextMessage message={props.message} sibling={props.sibling} />
                            </ChatMessagePart>
                        }

                        {msg.html &&
                            <ChatMessagePart showAvatar={avaKey === "html"} user={user}>
                                <ChatMarkdownMessage message={props.message} sibling={props.sibling} onOpenUrl={props.middlewareContext?.openUrl} />
                            </ChatMessagePart>
                        }

                        {msg.displays && middleware && msg.displays.map((display, index) => {
                            if ((display as any).type === "ScheduleButton") {
                                return (
                                    <ChatScheduleWidget minimizeOnClick={props.minimizeOnClick} display={display} />
                                )
                            }
                            const Middleware = middleware;
                            return (
                                <Middleware key={index} msg={display} ctx={props.middlewareContext} />
                            )
                        })}

                        {msg.permissionRequest && ctx &&
                            <ChatMessagePart showAvatar={avaKey === "permissionRequest"} user={user}>
                                <ChatPermissionMessage message={props.message} sibling={props.sibling} ctx={ctx} />
                            </ChatMessagePart>
                        }
                    </>
                );

            // TODO: do file response?
            // case "chat.file":
            //     return (
            //         <div className={`chat-msg-container ${getClassName(props.message)} ${props.addClass}`}>
            //             {renderAvatar(props.agent)}
            //             <div className="chat-msg-wrapper">
            //                 <ChatMedia message={messageDetail} />
            //                 {renderOptions(props.message.msg.options)}
            //             </div>
            //         </div>
            //     );
            default:
                break;
        }

        return (<></>);

    }

    function renderTimestamp() {

        const timestamp = props.message.timestamp;

        const ts = new Date(timestamp);

        const timeAgo = getTimeAgo(ts)

        return (
            <div className={`chat-msg-timestamp ${isAgent(props.message.user.nick) ? "agent" : "visitor"}`}>
                <span>{timeAgo}</span>
            </div>
        );
    }

    // empty
    return (
        <div className={`chat-msg-container-wrapper ${isAgent(props.message.user.nick) ? "agent" : "visitor"} ${props.sibling ? "sibling" : ""}`}>
            <div className={`chat-msg-container ${getClassName(props.message)}`}>
                {renderByType()}
                {renderTimestamp()}
            </div>
        </div>
    );
}

export default ChatMessage;
